import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from '../components/shared/ProductCard';
import { products } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { FiFilter, FiSearch, FiX } from 'react-icons/fi';

export default function Shop() {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading state for better UX realism
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, [filter, searchQuery]);

  const categories = [
    { id: 'all', label: 'All Items' },
    { id: 'box', label: 'Bento Boxes' },
    { id: 'accessories', label: 'Accessories' },
    { id: 'recipe-kit', label: 'Recipe Kits' },
    { id: 'bundle', label: 'Bundles' },
  ];

  const filteredProducts = products.filter(p => {
    const matchesFilter = filter === 'all' || p.category === filter;
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         p.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen">
      
      <div className="mb-12 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4 font-heading">Shop Bloom</h1>
          <p className="text-lg text-gray-600 max-w-xl">
            Curated essentials for your meal prep platform.
          </p>
        </div>
        
        {/* Search Bar */}
        <div className="relative w-full md:w-72">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text"
            placeholder="Search workspace..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 rounded-2xl focus:ring-2 focus:ring-bloom-coral focus:border-transparent outline-none transition-all shadow-sm"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-bloom-coral"
            >
              <SafeIcon icon={FiX} />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
        
        {/* Sidebar Filters */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm sticky top-28 border border-gray-50">
            <div className="flex items-center gap-2 font-bold text-lg mb-8 pb-4 border-b border-gray-100">
              <SafeIcon icon={FiFilter} />
              Filter By
            </div>
            <ul className="space-y-4">
              {categories.map(cat => (
                <li key={cat.id}>
                  <button
                    onClick={() => setFilter(cat.id)}
                    className={`w-full text-left px-5 py-3 rounded-2xl transition-all font-semibold text-sm tracking-wide ${
                      filter === cat.id 
                        ? 'bg-bloom-charcoal text-white shadow-lg' 
                        : 'text-gray-500 hover:bg-bloom-sand'
                    }`}
                  >
                    {cat.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Product Grid */}
        <div className="flex-grow">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(n => (
                <div key={n} className="animate-pulse bg-white rounded-4xl p-5 h-[450px]">
                  <div className="aspect-[4/5] bg-gray-100 rounded-3xl mb-6"></div>
                  <div className="h-6 bg-gray-100 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <AnimatePresence mode="popLayout">
                {filteredProducts.length > 0 ? (
                  <motion.div 
                    layout
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                  >
                    {filteredProducts.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </motion.div>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-32 bg-white rounded-[3rem] border-2 border-dashed border-gray-100 flex flex-col items-center"
                  >
                    <div className="w-20 h-20 bg-bloom-sand rounded-full flex items-center justify-center mb-6 text-gray-400">
                      <SafeIcon icon={FiSearch} className="text-3xl" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold mb-2">No items found</h3>
                    <p className="text-gray-500 mb-8">Try adjusting your filters or search query.</p>
                    <button 
                      onClick={() => { setFilter('all'); setSearchQuery(''); }}
                      className="bg-bloom-charcoal text-white px-8 py-3 rounded-2xl font-bold hover:bg-bloom-coral transition-colors"
                    >
                      Reset All Filters
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>

      </div>
    </div>
  );
}